/**
 * eReg Protocols Management Page
 *
 * Create, edit, and manage protocols.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Plus,
  FileText,
  MoreVertical,
  Edit,
  Trash2,
  AlertTriangle,
  Loader2,
  Archive,
  ArchiveRestore,
  ChevronDown,
  Check,
  Sparkles,
  UploadCloud,
  X,
} from 'lucide-react';
import { useEreg } from '../contexts/EregContext';
import { useAuth } from '../../contexts/AuthContext';
import * as eregApi from '../lib/api';
import type { StudyLinks } from '../lib/api';
import { PageHeader, SearchInput, FilterChip, ConfirmModal, fieldCls, useToast } from '../../ui';
import type { Folder, Protocol, ProtocolStatus, User } from '../types';

// Flatten the (possibly nested) folder tree the folders API returns.
function flattenFolders(nodes: Folder[]): Folder[] {
  const out: Folder[] = [];
  const walk = (arr: Folder[]) => {
    for (const n of arr) {
      out.push(n);
      if (n.children && n.children.length) walk(n.children);
    }
  };
  walk(nodes || []);
  return out;
}

/**
 * Best-effort: file a dropped protocol PDF into a freshly-created study.
 * Opening the study (getProtocol) triggers server-side enrollment, which
 * provisions the default TMF folder tree; we then upload into the folder that
 * accepts 'Protocol' documents — the upload auto-pins it to the protocol slot.
 * Throws on failure so the caller can surface a soft warning without blocking
 * study creation.
 */
async function fileProtocolIntoStudy(protocolId: string, file: File, protocolNumber?: string) {
  await eregApi.getProtocol(protocolId); // triggers ensureEregEnrollment → folder tree
  const folders = flattenFolders(await eregApi.getProtocolFolders(protocolId));
  const target =
    folders.find((f) => (f.allowed_document_types || []).includes('Protocol')) ||
    folders.find((f) => f.default_document_type === 'Protocol') ||
    folders.find((f) => /protocol/i.test(f.name)) ||
    folders[0];
  if (!target) throw new Error('No folder available to file the protocol into');

  const documentName =
    protocolNumber && protocolNumber.trim()
      ? `${protocolNumber.trim()} Protocol`
      : file.name.replace(/\.[^.]+$/, '');
  await eregApi.uploadDocument(file, {
    folder_id: target.id,
    document_name: documentName,
    document_type: 'Protocol',
  });
}

// eReg workflow status vocabulary (ereg_protocol_config.ereg_status) — kept in
// lock-step with the DB CHECK constraint and the Edit Study modal's dropdown.
const STATUS_OPTIONS: { value: ProtocolStatus; label: string }[] = [
  { value: 'startup', label: 'Startup' },
  { value: 'active', label: 'Active' },
  { value: 'closed', label: 'Closed' },
  { value: 'archived', label: 'Archived' },
];
const STATUS_LABEL: Record<string, string> = Object.fromEntries(
  STATUS_OPTIONS.map((o) => [o.value, o.label])
);
const statusPillCls = (status?: string) =>
  status === 'active'
    ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
    : status === 'startup'
      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
      : 'bg-gray-100 text-gray-700 dark:bg-gray-700/60 dark:text-gray-300';
const statusDotCls = (status?: string) =>
  status === 'active' ? 'bg-green-500' : status === 'startup' ? 'bg-yellow-500' : 'bg-gray-400';

// Inline status editor — the colored pill is a dropdown trigger, so a study's
// status can be changed straight from the table without opening Edit Details.
function StatusSelect({
  value,
  saving,
  onChange,
}: {
  value: ProtocolStatus;
  saving: boolean;
  onChange: (next: ProtocolStatus) => void;
}) {
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  const toggle = () => {
    // The table card is overflow-hidden, so flip the menu upward when there
    // isn't room below the pill (keeps the last rows' menus from clipping).
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setDropUp(window.innerHeight - rect.bottom < 180);
    }
    setOpen((v) => !v);
  };

  return (
    <div className="relative inline-block">
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        disabled={saving}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Change status"
        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60 ${statusPillCls(value)}`}
      >
        {saving && <Loader2 className="h-3 w-3 animate-spin" />}
        {STATUS_LABEL[value] ?? value}
        <ChevronDown className="h-3 w-3 opacity-60" />
      </button>

      {open && (
        <>
          {/* click-away backdrop */}
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            role="listbox"
            className={`absolute left-0 z-20 w-36 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800 ${
              dropUp ? 'bottom-full mb-1' : 'top-full mt-1'
            }`}
          >
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={opt.value === value}
                onClick={() => {
                  setOpen(false);
                  onChange(opt.value);
                }}
                className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                <span className="inline-flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${statusDotCls(opt.value)}`} />
                  {opt.label}
                </span>
                {opt.value === value && (
                  <Check className="h-3.5 w-3.5 text-primary-600 dark:text-primary-400" />
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function Protocols() {
  const { refreshProtocols, currentProtocol, setCurrentProtocol } = useEreg();
  const { currentSite, availableSites, isSuperAdmin } = useAuth();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedProtocol, setSelectedProtocol] = useState<Protocol | null>(null);
  const [menuOpen, setMenuOpen] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Protocol | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<Protocol | null>(null);
  const [archiving, setArchiving] = useState(false);

  // This admin page needs archived studies too (toggle + count), so it keeps
  // its own fetch of the full list instead of the shared EregContext.protocols,
  // which is active-only (My Studies / dropdowns must never see archived rows).
  const [allProtocols, setAllProtocols] = useState<Protocol[]>([]);
  const [allProtocolsLoading, setAllProtocolsLoading] = useState(true);
  const refreshAllProtocols = useCallback(async () => {
    setAllProtocolsLoading(true);
    try {
      const data = await eregApi.getProtocols({ includeArchived: true });
      setAllProtocols(data);
    } catch (error) {
      console.error('[eReg] Error loading studies:', error);
    } finally {
      setAllProtocolsLoading(false);
    }
  }, []);
  useEffect(() => {
    refreshAllProtocols();
  }, [refreshAllProtocols, currentSite?.id]);

  // Keep the shared context list (My Studies, dropdowns, etc.) in sync too.
  const refreshBoth = useCallback(async () => {
    await Promise.all([refreshProtocols(), refreshAllProtocols()]);
  }, [refreshProtocols, refreshAllProtocols]);

  const archivedCount = allProtocols.filter((p) => p.archived_at).length;

  // Inline status edits: optimistic local overrides so the table never flashes
  // its loading state on a one-field change. Cleared whenever the list refetches
  // (the fresh rows already carry the new status).
  const [statusOverrides, setStatusOverrides] = useState<Record<string, ProtocolStatus>>({});
  const [statusSaving, setStatusSaving] = useState<string | null>(null);
  useEffect(() => setStatusOverrides({}), [allProtocols]);

  const effectiveStatus = (p: Protocol): ProtocolStatus =>
    statusOverrides[p.id] ?? ((p.status as ProtocolStatus) || 'startup');

  const handleStatusChange = async (protocol: Protocol, next: ProtocolStatus) => {
    if (next === effectiveStatus(protocol)) return;
    setStatusOverrides((o) => ({ ...o, [protocol.id]: next }));
    setStatusSaving(protocol.id);
    try {
      await eregApi.updateProtocol(protocol.id, { status: next });
      toast(`Status set to ${STATUS_LABEL[next]}`);
    } catch {
      // Revert the optimistic value on failure.
      setStatusOverrides((o) => {
        const rest = { ...o };
        delete rest[protocol.id];
        return rest;
      });
      toast('Failed to update the status', { type: 'error' });
    } finally {
      setStatusSaving((s) => (s === protocol.id ? null : s));
    }
  };

  // Filter by archived state (hidden by default) + search
  const filteredProtocols = allProtocols.filter((p) => {
    if (!showArchived && p.archived_at) return false;
    return (
      p.protocol_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.protocol_title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.sponsor_name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  // Archive / restore a study (registry soft-archive).
  const handleArchive = async (protocol: Protocol, archived: boolean) => {
    setArchiving(true);
    try {
      await eregApi.archiveProtocol(protocol.id, archived);
      await refreshBoth();
      toast(
        archived
          ? `Archived ${protocol.protocol_number}`
          : `Restored ${protocol.protocol_number}`
      );
    } catch {
      toast(`Failed to ${archived ? 'archive' : 'restore'} the study`, { type: 'error' });
    } finally {
      setArchiving(false);
      setArchiveTarget(null);
    }
  };

  // Create study handler — throws so the modal can surface 409 duplicates.
  // When a protocol PDF was dropped, file it into the new study (best-effort:
  // study creation still succeeds even if filing fails).
  const handleCreateProtocol = async (
    data: Partial<Protocol> & { allow_duplicate?: boolean },
    protocolFile?: File | null
  ) => {
    const created = await eregApi.createProtocol({
      ...data,
      site_id: data.site_id || currentSite?.id,
    });

    let fileFailed = false;
    if (protocolFile) {
      try {
        await fileProtocolIntoStudy(created.id, protocolFile, created.protocol_number || data.protocol_number);
      } catch (fileErr) {
        console.error('[eReg] Failed to auto-file the protocol document:', fileErr);
        fileFailed = true;
      }
    }

    await refreshBoth();
    setShowCreateModal(false);
    if (fileFailed) {
      toast('Study created, but filing the protocol failed — upload it from the study.', { type: 'error' });
    } else {
      toast(`Created ${created.protocol_number}${protocolFile ? ' — protocol filed' : ''}`);
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <PageHeader
        title="Manage Studies"
        subtitle="Create and configure study document repositories"
        actions={
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150"
          >
            <Plus size={18} />
            <span>New Study</span>
          </button>
        }
      />

      {/* Search + archived toggle */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-[200px] flex-1">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search studies..."
            label="Search studies"
          />
        </div>
        {archivedCount > 0 && (
          <FilterChip
            active={showArchived}
            onClick={() => setShowArchived((v) => !v)}
            icon={<Archive className="h-4 w-4" />}
            title="Show archived studies"
          >
            Show archived ({archivedCount})
          </FilterChip>
        )}
      </div>

      {/* Protocols Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden dark:bg-gray-800 dark:border-gray-700">
        {allProtocolsLoading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 dark:border-primary-400"></div>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading studies...</p>
          </div>
        ) : filteredProtocols.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            <FileText size={40} className="mx-auto mb-2 text-gray-300 dark:text-gray-600" />
            <p>No studies found</p>
            {searchQuery && (
              <p className="text-sm mt-1">Try a different search term</p>
            )}
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Study
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Sponsor
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Site
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  PI
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
              {filteredProtocols.map((protocol) => (
                <tr
                  key={protocol.id}
                  className={`transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                    protocol.archived_at ? 'opacity-60' : ''
                  }`}
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {protocol.protocol_number}
                        </span>
                        {protocol.archived_at && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                            <Archive size={10} />
                            Archived
                          </span>
                        )}
                      </div>
                      {protocol.protocol_title && (
                        <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                          {protocol.protocol_title}
                        </div>
                      )}
                      {protocol.crio_study_id && (
                        <div
                          className="mt-0.5 text-xs text-gray-500 tabular-nums dark:text-gray-400"
                          title="CRIO study ID — shared with the CRM and IXVerify"
                        >
                          CRIO {protocol.crio_study_id}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {protocol.sponsor_name || '-'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {protocol.site?.name || '-'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <StatusSelect
                      value={effectiveStatus(protocol)}
                      saving={statusSaving === protocol.id}
                      onChange={(next) => handleStatusChange(protocol, next)}
                    />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {protocol.primary_investigator?.full_name || '-'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm relative">
                    <button
                      onClick={() => setMenuOpen(menuOpen === protocol.id ? null : protocol.id)}
                      className="p-1 rounded transition-colors duration-150 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <MoreVertical size={18} className="text-gray-400 dark:text-gray-500" />
                    </button>

                    {menuOpen === protocol.id && (
                      <div className="absolute right-6 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10 dark:bg-gray-800 dark:border-gray-700">
                        <button
                          onClick={() => {
                            // Seed the modal with the effective (optimistically
                            // overridden) status so a subsequent save can't write
                            // a stale status back over an inline change.
                            setSelectedProtocol({ ...protocol, status: effectiveStatus(protocol) });
                            setMenuOpen(null);
                          }}
                          className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                        >
                          <Edit size={16} />
                          Edit Details
                        </button>
                        {/* "Manage Access" / "Configure" were no-op buttons —
                            removed until those features exist. */}
                        <hr className="my-1 border-gray-200 dark:border-gray-700" />
                        {protocol.archived_at ? (
                          <button
                            onClick={() => {
                              setMenuOpen(null);
                              handleArchive(protocol, false);
                            }}
                            className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                          >
                            <ArchiveRestore size={16} />
                            Unarchive
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              setArchiveTarget(protocol);
                              setMenuOpen(null);
                            }}
                            className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                          >
                            <Archive size={16} />
                            Archive
                          </button>
                        )}
                        {/* Hard delete — super_admin only (also enforced server-side). */}
                        {isSuperAdmin && (
                          <button
                            onClick={() => {
                              setDeleteTarget(protocol);
                              setMenuOpen(null);
                            }}
                            className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 transition-colors duration-150 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                          >
                            <Trash2 size={16} />
                            Delete Study…
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Modal - simplified for now */}
      {showCreateModal && (
        <CreateProtocolModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateProtocol}
          sites={availableSites}
          currentSiteId={currentSite?.id}
        />
      )}

      {/* Edit Study modal — PI/CRC/status/IRB (audit S-3: this was a dead
          button; the PI field here is what unblocks DOA countersigns). */}
      {selectedProtocol && (
        <EditProtocolModal
          protocol={selectedProtocol}
          onClose={() => setSelectedProtocol(null)}
          onSaved={async (updated) => {
            setSelectedProtocol(null);
            // A CRIO id saved here also writes to CRM + IXVerify. If a mirror
            // leg failed the study still saved, but say so — a number that
            // looks synced and is not is the failure mode worth surfacing.
            const crioWarnings = (updated as Protocol & { crio_sync_warnings?: string[] }).crio_sync_warnings;
            if (crioWarnings?.length) {
              toast(`Updated ${updated.protocol_number}, but the CRIO ID did not reach every tool: ${crioWarnings.join(' ')}`, { type: 'error' });
            } else {
              toast(`Updated ${updated.protocol_number}`);
            }
            await refreshBoth();
            // Keep the module-wide selection in sync when the edited study is
            // the one currently open (PI banner on DelegationLog reads it).
            if (currentProtocol?.id === updated.id) {
              setCurrentProtocol(updated);
            }
          }}
        />
      )}

      {/* Delete Study confirmation — super_admin only */}
      {deleteTarget && (
        <DeleteStudyModal
          protocol={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={async () => {
            setDeleteTarget(null);
            await refreshBoth();
          }}
        />
      )}

      {/* Archive confirmation (reversible) */}
      <ConfirmModal
        isOpen={!!archiveTarget}
        onClose={() => {
          if (!archiving) setArchiveTarget(null);
        }}
        onConfirm={() => {
          if (archiveTarget) handleArchive(archiveTarget, true);
        }}
        title="Archive study?"
        message={
          <>
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {archiveTarget?.protocol_number}
            </span>{' '}
            will be hidden from the studies list and from study pickers in other tools. Its documents
            and existing links stay intact, and you can restore it anytime.
          </>
        }
        confirmLabel="Archive"
        loading={archiving}
      />
    </div>
  );
}

// Simple create protocol modal
function CreateProtocolModal({
  onClose,
  onSubmit,
  sites,
  currentSiteId,
}: {
  onClose: () => void;
  onSubmit: (
    data: Partial<Protocol> & { allow_duplicate?: boolean },
    protocolFile?: File | null
  ) => Promise<void>;
  sites: Array<{ id: string; name: string }>;
  currentSiteId?: string;
}) {
  const [formData, setFormData] = useState({
    site_id: currentSiteId || '',
    protocol_number: '',
    protocol_title: '',
    sponsor_name: '',
    therapeutic_area: '',
    phase: '',
    irb_of_record: '',
    crio_study_id: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [protocolFile, setProtocolFile] = useState<File | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Drop the protocol PDF → AI prefills the study fields; the file is retained
  // and filed into the study on create.
  const handleFileSelect = async (file: File) => {
    setProtocolFile(file);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (file.type !== 'application/pdf') return; // AI extraction is PDF-only
    setAnalyzing(true);
    try {
      const { fields } = await eregApi.suggestStudyFields(file);
      setFormData((prev) => ({
        ...prev,
        protocol_number: fields.protocol_number || prev.protocol_number,
        protocol_title: fields.protocol_title || prev.protocol_title,
        sponsor_name: fields.sponsor_name || prev.sponsor_name,
        therapeutic_area: fields.therapeutic_area || prev.therapeutic_area,
        phase: fields.phase || prev.phase,
        irb_of_record: fields.irb_of_record || prev.irb_of_record,
      }));
    } catch (err) {
      console.error('Study-field extraction failed:', err);
      // Non-fatal — the user can fill the form manually.
    } finally {
      setAnalyzing(false);
    }
  };

  const submitWith = async (allowDuplicate: boolean) => {
    if (!formData.protocol_number || !formData.site_id) return;
    setLoading(true);
    setError(null);
    try {
      await onSubmit(allowDuplicate ? { ...formData, allow_duplicate: true } : formData, protocolFile);
    } catch (err: any) {
      // Soft-uniqueness guard (§3.1): surface the duplicate + allow override.
      if (err?.response?.status === 409 && err.response.data?.code === 'EREG_DUPLICATE_PROTOCOL') {
        setIsDuplicate(true);
        setError('A study with this protocol number already exists at this site.');
      } else {
        setIsDuplicate(false);
        setError('Failed to create study. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitWith(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto dark:bg-gray-800">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Create New Study</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Drop the protocol PDF → AI prefills the fields + files it on create */}
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const dropped = e.dataTransfer.files?.[0];
              if (dropped) handleFileSelect(dropped);
            }}
            className={`rounded-lg border-2 border-dashed p-4 text-center transition-colors duration-150 ${
              protocolFile
                ? 'border-primary-300 bg-primary-50 dark:border-primary-800 dark:bg-primary-900/20'
                : 'border-gray-300 hover:border-primary-400 dark:border-gray-600 dark:hover:border-primary-500'
            }`}
          >
            {protocolFile ? (
              <div className="flex items-center justify-center gap-2">
                <FileText size={18} className="flex-shrink-0 text-primary-600 dark:text-primary-400" />
                <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                  {protocolFile.name}
                </span>
                <button
                  type="button"
                  onClick={() => setProtocolFile(null)}
                  className="rounded p-1 hover:bg-primary-100 dark:hover:bg-primary-900/40"
                  aria-label="Remove file"
                >
                  <X size={16} className="text-gray-500 dark:text-gray-400" />
                </button>
              </div>
            ) : (
              <>
                <UploadCloud size={28} className="mx-auto mb-1.5 text-gray-400 dark:text-gray-500" />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Drop the protocol PDF here, or{' '}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                  >
                    browse
                  </button>
                </p>
                <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
                  AI fills in the fields below and files it into the study.
                </p>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
              className="hidden"
            />
          </div>

          {analyzing && (
            <div className="flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400">
              <Sparkles size={16} className="animate-pulse" />
              <span>Reading the protocol and filling in fields…</span>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Site *
            </label>
            <select
              value={formData.site_id}
              onChange={(e) => setFormData({ ...formData, site_id: e.target.value })}
              className={`w-full ${fieldCls}`}
              required
            >
              <option value="">Select site...</option>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>{site.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Protocol Number *
            </label>
            <input
              type="text"
              value={formData.protocol_number}
              onChange={(e) => setFormData({ ...formData, protocol_number: e.target.value })}
              placeholder="e.g., ABC-123"
              className={`w-full ${fieldCls}`}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Study Title
            </label>
            <input
              type="text"
              value={formData.protocol_title}
              onChange={(e) => setFormData({ ...formData, protocol_title: e.target.value })}
              placeholder="Full study title"
              className={`w-full ${fieldCls}`}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Sponsor
              </label>
              <input
                type="text"
                value={formData.sponsor_name}
                onChange={(e) => setFormData({ ...formData, sponsor_name: e.target.value })}
                className={`w-full ${fieldCls}`}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Phase
              </label>
              <select
                value={formData.phase}
                onChange={(e) => setFormData({ ...formData, phase: e.target.value })}
                className={`w-full ${fieldCls}`}
              >
                <option value="">Select...</option>
                <option value="I">Phase I</option>
                <option value="II">Phase II</option>
                <option value="III">Phase III</option>
                <option value="IV">Phase IV</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Therapeutic Area
            </label>
            <input
              type="text"
              value={formData.therapeutic_area}
              onChange={(e) => setFormData({ ...formData, therapeutic_area: e.target.value })}
              className={`w-full ${fieldCls}`}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              IRB of Record
            </label>
            <input
              type="text"
              value={formData.irb_of_record}
              onChange={(e) => setFormData({ ...formData, irb_of_record: e.target.value })}
              className={`w-full ${fieldCls}`}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="new-study-crio">
              CRIO Study ID
            </label>
            <input
              id="new-study-crio"
              type="text"
              inputMode="numeric"
              maxLength={100}
              placeholder="e.g. 32718"
              value={formData.crio_study_id}
              onChange={(e) => setFormData({ ...formData, crio_study_id: e.target.value })}
              className={`w-full ${fieldCls}`}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Optional — the study&rsquo;s ID in CRIO. Shared with the CRM and IXVerify once they link to this study.
            </p>
          </div>

          {error && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
              {error}
              {isDuplicate && (
                <button
                  type="button"
                  onClick={() => submitWith(true)}
                  disabled={loading}
                  className="ml-2 inline-flex min-h-[44px] items-center text-xs font-medium underline sm:min-h-0"
                >
                  Create anyway
                </button>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-150 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.protocol_number || !formData.site_id}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Study'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Edit Study modal — writes through PATCH /protocols/:id (protocol fields +
// ereg_protocol_config fields). The PI picker is the only place a study's
// Primary Investigator can be set, and PI countersigns on the delegation log
// are blocked until it is.
function EditProtocolModal({
  protocol,
  onClose,
  onSaved,
}: {
  protocol: Protocol;
  onClose: () => void;
  onSaved: (updated: Protocol) => void | Promise<void>;
}) {
  const [formData, setFormData] = useState({
    protocol_title: protocol.protocol_title || '',
    sponsor_name: protocol.sponsor_name || '',
    therapeutic_area: protocol.therapeutic_area || '',
    phase: protocol.phase || '',
    status: protocol.status || 'startup',
    irb_of_record: protocol.irb_of_record || '',
    crio_study_id: protocol.crio_study_id || '',
    primary_investigator_id: protocol.primary_investigator_id || '',
    primary_crc_id: protocol.primary_crc_id || '',
  });
  const [siteUsers, setSiteUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setUsersLoading(true);
    eregApi
      .getEligibleSigners(protocol.site_id)
      .then((users) => {
        if (active) setSiteUsers(users);
      })
      .catch((err) => {
        console.error('Failed to load site users:', err);
        if (active) setSiteUsers([]);
      })
      .finally(() => {
        if (active) setUsersLoading(false);
      });
    return () => {
      active = false;
    };
  }, [protocol.site_id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const updated = await eregApi.updateProtocol(protocol.id, {
        protocol_title: formData.protocol_title || undefined,
        sponsor_name: formData.sponsor_name || undefined,
        therapeutic_area: formData.therapeutic_area || undefined,
        phase: formData.phase || undefined,
        status: formData.status as Protocol['status'],
        irb_of_record: formData.irb_of_record || undefined,
        // Always sent (empty string clears it) — unlike the fields above, the
        // CRIO id has to be clearable, and clearing it must reach CRM/IXVerify.
        crio_study_id: formData.crio_study_id.trim(),
        // Explicit null clears the assignment server-side.
        primary_investigator_id: (formData.primary_investigator_id || null) as any,
        primary_crc_id: (formData.primary_crc_id || null) as any,
      });
      await onSaved(updated);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to update the study. Please try again.');
      setSaving(false);
    }
  };

  const userOptions = siteUsers.map((user) => (
    <option key={user.id} value={user.id}>
      {user.full_name} ({user.email})
    </option>
  ));

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto dark:bg-gray-800">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Edit Study</h2>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{protocol.protocol_number}</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Study Title
            </label>
            <input
              type="text"
              value={formData.protocol_title}
              onChange={(e) => setFormData({ ...formData, protocol_title: e.target.value })}
              className={`w-full ${fieldCls}`}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Sponsor
              </label>
              <input
                type="text"
                value={formData.sponsor_name}
                onChange={(e) => setFormData({ ...formData, sponsor_name: e.target.value })}
                className={`w-full ${fieldCls}`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Phase
              </label>
              <select
                value={formData.phase}
                onChange={(e) => setFormData({ ...formData, phase: e.target.value })}
                className={`w-full ${fieldCls}`}
              >
                <option value="">Select...</option>
                <option value="I">Phase I</option>
                <option value="II">Phase II</option>
                <option value="III">Phase III</option>
                <option value="IV">Phase IV</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className={`w-full ${fieldCls}`}
              >
                <option value="startup">Startup</option>
                <option value="active">Active</option>
                <option value="closed">Closed</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Therapeutic Area
              </label>
              <input
                type="text"
                value={formData.therapeutic_area}
                onChange={(e) => setFormData({ ...formData, therapeutic_area: e.target.value })}
                className={`w-full ${fieldCls}`}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Primary Investigator
            </label>
            <select
              value={formData.primary_investigator_id}
              onChange={(e) => setFormData({ ...formData, primary_investigator_id: e.target.value })}
              className={`w-full ${fieldCls}`}
              disabled={usersLoading}
            >
              <option value="">{usersLoading ? 'Loading users…' : 'Not set'}</option>
              {userOptions}
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Required for PI countersigns on the delegation log.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Primary CRC
            </label>
            <select
              value={formData.primary_crc_id}
              onChange={(e) => setFormData({ ...formData, primary_crc_id: e.target.value })}
              className={`w-full ${fieldCls}`}
              disabled={usersLoading}
            >
              <option value="">{usersLoading ? 'Loading users…' : 'Not set'}</option>
              {userOptions}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              IRB of Record
            </label>
            <input
              type="text"
              value={formData.irb_of_record}
              onChange={(e) => setFormData({ ...formData, irb_of_record: e.target.value })}
              className={`w-full ${fieldCls}`}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="edit-study-crio">
              CRIO Study ID
            </label>
            <input
              id="edit-study-crio"
              type="text"
              inputMode="numeric"
              maxLength={100}
              placeholder="e.g. 32718"
              value={formData.crio_study_id}
              onChange={(e) => setFormData({ ...formData, crio_study_id: e.target.value })}
              className={`w-full ${fieldCls}`}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              The study&rsquo;s ID in CRIO — the key that lines SiteOps up with the CRIO API.
              Shared with the CRM and IXVerify: change it here and it changes there too.
            </p>
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-150 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 disabled:opacity-50"
            >
              {saving && <Loader2 size={16} className="animate-spin" />}
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Super-admin hard-delete confirmation modal. Shows the study's cross-module
// "used by" footprint and requires typing the exact protocol number to confirm.
function DeleteStudyModal({
  protocol,
  onClose,
  onDeleted,
}: {
  protocol: Protocol;
  onClose: () => void;
  onDeleted: () => void | Promise<void>;
}) {
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [links, setLinks] = useState<StudyLinks | null>(null);
  const [linksLoading, setLinksLoading] = useState(true);

  const target = (protocol.protocol_number || '').trim();
  const canDelete = confirmText.trim() === target && target.length > 0 && !loading;

  useEffect(() => {
    let active = true;
    setLinksLoading(true);
    eregApi
      .getStudyLinks(protocol.id)
      .then((data) => {
        if (active) setLinks(data);
      })
      .catch(() => {
        // Non-fatal: the confirmation still works without the "used by" preview.
        if (active) setLinks(null);
      })
      .finally(() => {
        if (active) setLinksLoading(false);
      });
    return () => {
      active = false;
    };
  }, [protocol.id]);

  // Cross-module references worth surfacing before an irreversible delete.
  const usedBy: Array<{ label: string; detail: string }> = [];
  if (links) {
    if (links.crm.length) usedBy.push({ label: 'CRM studies', detail: String(links.crm.length) });
    if (links.ixverify.length) usedBy.push({ label: 'IXVerify studies', detail: String(links.ixverify.length) });
    if (links.status_board.length) {
      usedBy.push({ label: 'Status Board mappings', detail: links.status_board.join(', ') });
    }
    if (links.organizer.team_members) {
      usedBy.push({ label: 'Organizer team members', detail: String(links.organizer.team_members) });
    }
    if (links.organizer.bookmarks) {
      usedBy.push({ label: 'Organizer bookmarks', detail: String(links.organizer.bookmarks) });
    }
    if (links.intelligence_documents) {
      usedBy.push({ label: 'Intelligence documents', detail: String(links.intelligence_documents) });
    }
    if (links.mhr) usedBy.push({ label: 'MHR protocols', detail: String(links.mhr) });
  }

  const handleDelete = async () => {
    if (!canDelete) return;
    setLoading(true);
    setError(null);
    try {
      // The super_admin has already seen the "used by" footprint, so force past
      // the server's in-use guard.
      await eregApi.deleteProtocol(protocol.id, confirmText.trim(), { force: true });
      await onDeleted();
    } catch (err: any) {
      const code = err?.response?.data?.code;
      if (code === 'EREG_DELETE_CONFIRM_MISMATCH') {
        setError('The protocol number did not match. Type it exactly as shown.');
      } else {
        setError(err?.response?.data?.error || 'Failed to delete study. Please try again.');
      }
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !loading) onClose();
      }}
    >
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto dark:bg-gray-800">
        <div className="flex items-start gap-3 px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
            <AlertTriangle size={18} className="text-red-600 dark:text-red-400" />
          </span>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Delete study</h2>
            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              This permanently deletes the study, its folders, documents, versions, signatures, and
              delegation log. This cannot be undone.
            </p>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-900/40">
            <div className="font-medium text-gray-900 dark:text-gray-100">{protocol.protocol_number}</div>
            {(protocol.study_nickname || protocol.protocol_title) && (
              <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                {protocol.study_nickname || protocol.protocol_title}
              </div>
            )}
          </div>

          {/* Cross-module blast radius */}
          {linksLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <Loader2 size={16} className="animate-spin text-primary-600 dark:text-primary-400" />
              Checking where this study is used…
            </div>
          ) : usedBy.length > 0 ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm dark:border-amber-800 dark:bg-amber-900/20">
              <div className="flex items-center gap-1.5 font-medium text-amber-800 dark:text-amber-300">
                <AlertTriangle size={14} />
                This study is still used by other tools
              </div>
              <ul className="mt-1.5 space-y-1 text-amber-800/90 dark:text-amber-300/90">
                {usedBy.map((u) => (
                  <li key={u.label} className="flex justify-between gap-3">
                    <span>{u.label}</span>
                    <span className="font-medium tabular-nums">{u.detail}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-1.5 text-xs text-amber-700/90 dark:text-amber-400/80">
                Deleting will unlink these references (CRM/IXVerify/MHR/Status Board) and remove the
                study's Organizer and Intelligence data.
              </p>
            </div>
          ) : null}

          <div>
            <label htmlFor="delete-confirm" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Type <span className="font-semibold text-gray-900 dark:text-gray-100">{protocol.protocol_number}</span> to confirm
            </label>
            <input
              id="delete-confirm"
              type="text"
              autoFocus
              autoComplete="off"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canDelete) handleDelete();
              }}
              placeholder={protocol.protocol_number}
              className={`w-full ${fieldCls}`}
            />
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-150 disabled:opacity-50 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={!canDelete}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {loading ? 'Deleting…' : 'Delete study'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
